import { Container, Graphics, Sprite, Texture } from 'pixi.js';
import gsap from 'gsap';
import type { ColorType } from '@shared/types';
import { NEON_COLORS } from '../colors';
import { HOLD_RECT_W, HOLD_RECT_H } from '@shared/gameConfig';

// Box the grayscale skin sprite fits inside (aspect-preserved). A touch wider
// than the drawn arc so the custom shape reads at a glance.
const SKIN_BOX_W = HOLD_RECT_W + 10;
const SKIN_BOX_H = 30;

type HoldState = 'past' | 'current' | 'future';

/**
 * A single climbing hold rendered in Pixi. The "ladder-rung" pill shape
 * the prototype uses — chiseled rock with chalk-dust dots and a glowing
 * aura ring when it's the next target.
 */
export class PixiHold extends Container {
  private body: Graphics;
  // Optional uploaded skin shape (grayscale PNG) tinted to the hold color.
  // When set, it replaces the drawn arc; the color signal stays via tint.
  private shapeSprite: Sprite;
  private shapeTex: Texture | null;
  private auraRing: Graphics;
  private flashOverlay: Graphics;
  private auraTime = 0;
  private flashTween?: gsap.core.Timeline;

  public color: ColorType;
  public state: HoldState = 'future';

  constructor(color: ColorType, shapeTex: Texture | null = null) {
    super();
    this.color = color;
    this.shapeTex = shapeTex;
    this.body = new Graphics();
    this.shapeSprite = new Sprite();
    this.shapeSprite.anchor.set(0.5);
    this.shapeSprite.visible = false;
    this.auraRing = new Graphics();
    this.flashOverlay = new Graphics();
    this.addChild(this.body);
    this.addChild(this.shapeSprite);
    this.addChild(this.auraRing);
    this.addChild(this.flashOverlay);
    this.drawBody();
    this.flashOverlay.visible = false;
  }

  /** The node currently carrying the hold visual (sprite skin or drawn arc). */
  private get visual(): Container {
    return this.shapeTex ? this.shapeSprite : this.body;
  }

  /** Swap the shape texture at runtime (e.g. when the match resolves the
   * player's equipped skin / random default). null → back to the drawn arc. */
  setShapeTexture(tex: Texture | null): void {
    this.shapeTex = tex;
    this.drawBody();
    if (this.state === 'past') this.visual.alpha = 0.32;
  }

  /**
   * Swap to a different color WITHOUT destroying the hold. The original
   * implementation replaced the entire PixiHold instance which raced against
   * any in-flight flash/aura tween → caused crashes when an opponent's
   * Disrupt landed at the wrong moment. Mutating in place is safer.
   */
  recolor(next: ColorType): void {
    if (this.color === next) return;
    this.color = next;
    this.drawBody();
    if (this.state === 'current') this.drawAura();
  }

  private drawBody(): void {
    if (this.shapeTex) {
      this.body.visible = false;
      const s = this.shapeSprite;
      s.visible = true;
      s.texture = this.shapeTex;
      s.tint = NEON_COLORS[this.color].base;
      const tw = this.shapeTex.width || SKIN_BOX_W;
      const th = this.shapeTex.height || SKIN_BOX_H;
      const k = Math.min(SKIN_BOX_W / tw, SKIN_BOX_H / th);
      s.scale.set(k);
      s.position.set(0, 2);
      return;
    }
    this.shapeSprite.visible = false;
    this.body.visible = true;
    this.drawArc();
  }

  private drawArc(): void {
    // Sketched concave arc ("⌣") — like a tiny ledge or chalk mark on the
    // rock face, not a solid pill. Matches the hand-drawn reference: small
    // curve marks, mostly stroke. The arc dips ~3-4px below the anchor so
    // the visual sits centred on the climber's path.
    const c = NEON_COLORS[this.color];
    const w = HOLD_RECT_W;
    const dip = 4;
    const g = this.body;
    g.clear();

    // Subtle shadow line under the arc for depth.
    g.moveTo(-w / 2 + 2, dip + 1.5)
      .quadraticCurveTo(0, dip + 4, w / 2 - 2, dip + 1.5)
      .stroke({ width: 1.5, color: 0x000000, alpha: 0.35 });

    // Main arc — colored stroke, the "ledge" itself.
    g.moveTo(-w / 2, 0)
      .quadraticCurveTo(0, dip, w / 2, 0)
      .stroke({ width: 2.5, color: c.base, cap: 'round', join: 'round' });

    // Highlight on top of the arc (sun-lit edge).
    g.moveTo(-w / 2 + 2, -0.5)
      .quadraticCurveTo(0, dip - 1.5, w / 2 - 2, -0.5)
      .stroke({ width: 1, color: c.light, alpha: 0.9, cap: 'round' });
  }

  setState(next: HoldState): void {
    if (this.state === next) return;
    this.state = next;
    // A state change overrides any in-progress flash — otherwise a Disrupt
    // warning could continue strobing on a hold the climber already passed.
    this.stopFlash();

    if (next === 'past') {
      this.visual.alpha = 0.32;
      this.auraRing.clear();
      this.auraRing.visible = false;
    } else if (next === 'current') {
      this.visual.alpha = 1;
      this.drawAura();
      this.auraRing.visible = true;
    } else {
      this.visual.alpha = 1;
      this.auraRing.clear();
      this.auraRing.visible = false;
    }
  }

  private drawAura(): void {
    const c = NEON_COLORS[this.color];
    const g = this.auraRing;
    g.clear();
    g.ellipse(0, 0, 42, 18)
      .stroke({ color: c.base, width: 2.5, alpha: 0.92 })
      .fill({ color: c.base, alpha: 0.06 });
  }

  /** Pulse the aura ring (called once per frame from the scene's ticker). */
  update(dt: number): void {
    if (this.state !== 'current') return;
    this.auraTime += dt;
    // ~0.85s loop, +/- 5%
    const k = (Math.sin((this.auraTime / 0.85) * Math.PI * 2) + 1) / 2;
    const scale = 1 + k * 0.1;
    this.auraRing.scale.set(scale);
  }

  /**
   * Strobe the hold for `durationMs` to warn the player a Disrupt is about
   * to reshuffle it. Caller is expected to call `stopFlash()` (or wait for the
   * tween to finish) before swapping the hold's color.
   */
  startFlash(durationMs: number): void {
    this.stopFlash();
    const g = this.flashOverlay;
    g.clear();
    // Flash overlay sits behind the arc — a translucent oval blob so the
    // strobe is visible without breaking the sketched look.
    g.ellipse(0, 2, HOLD_RECT_W / 2 + 2, HOLD_RECT_H / 2 + 3)
      .fill({ color: 0xffffff, alpha: 1 });
    g.visible = true;
    g.alpha = 0;

    const seconds = durationMs / 1000;
    // 4 strobe pulses across the warning window.
    const pulse = 0.08;
    const cycles = Math.max(2, Math.floor(seconds / 0.18));
    const cycleS = seconds / cycles;
    const tl = gsap.timeline({
      onComplete: () => this.stopFlash(),
    });
    for (let i = 0; i < cycles; i++) {
      tl.to(g, { alpha: 0.85, duration: cycleS * 0.45, ease: 'sine.in' });
      tl.to(g, { alpha: 0, duration: cycleS * 0.55, ease: 'sine.out' });
    }
    // Subtle scale pop so the warning is felt, not just seen. Animate the
    // whole hold container (rest scale 1 in both arc + sprite modes) so the
    // pulse works regardless of the sprite's aspect-fit scale.
    gsap.fromTo(
      this.scale,
      { x: 1, y: 1 },
      {
        x: 1 + pulse,
        y: 1 + pulse,
        duration: cycleS,
        yoyo: true,
        repeat: cycles * 2 - 1,
        ease: 'sine.inOut',
      },
    );
    this.flashTween = tl;
  }

  stopFlash(): void {
    this.flashTween?.kill();
    this.flashTween = undefined;
    if (!this.destroyed) {
      gsap.killTweensOf(this.scale);
      this.scale.set(1);
    }
    if (this.flashOverlay && !this.flashOverlay.destroyed) {
      this.flashOverlay.visible = false;
      this.flashOverlay.alpha = 0;
    }
  }

  /**
   * Pixi v8 throws when properties are read on destroyed display objects, so a
   * GSAP tween that outlives its target hold will crash the whole scene. Kill
   * every active tween first, then let the base destroy clean the children up.
   */
  override destroy(options?: Parameters<Container['destroy']>[0]): void {
    this.stopFlash();
    super.destroy(options);
  }
}
